import { supabase } from '@/lib/supabaseClient';
import { API_URL } from '@/config/api';

// ============================================
// MAPPING TABLES: Polish UI values -> English DB keys
// ============================================

// Muscle groups: Polish display name -> DB key
const MUSCLE_PL_TO_KEY: Record<string, string> = {
    // Standardized User Mapping
    'brzuch': 'abdominals',
    'barki': 'shoulders',
    'klatka piersiowa': 'chest',
    'klatka': 'chest', // Alias
    'czworogłowe ud': 'quadriceps',
    'czworogłowe': 'quadriceps', // Alias
    'dwugłowe ud': 'hamstrings',
    'dwugłowe': 'hamstrings', // Alias
    'triceps': 'triceps',
    'biceps': 'biceps',
    'najszersze grzbietu': 'lats',
    'środkowe pleców': 'middle back',
    'łydki': 'calves',
    'lędźwie': 'lower back',
    'przedramiona': 'forearms',
    'pośladki': 'glutes',
    'kaptury': 'traps',
    'przywodziciele': 'adductors',
    'odwodziciele': 'abductors',
    'szyja': 'neck',
    'całe ciało': 'full body',
    'cardio': 'cardio',

    // Legacy / Aliases / Fallbacks
    'plecy': 'back',
    'grzbiet': 'back',
    'prostowniki': 'lower back',
    'prostowniki grzbietu': 'lower back',
    'nogi': 'quadriceps',
    'czworoboczny': 'traps',

    // English keys pass through (ensure lowercase matches DB keys)
    'abdominals': 'abdominals',
    'shoulders': 'shoulders',
    'chest': 'chest',
    'back': 'back',
    'quadriceps': 'quadriceps',
    'hamstrings': 'hamstrings',
    'glutes': 'glutes',
    'calves': 'calves',
    'lats': 'lats',
    'trap': 'traps', // Handle singular just in case
    'traps': 'traps',
    'lower_back': 'lower back', // map snake to space
    'lower back': 'lower back',
    'middle_back': 'middle back', // map snake to space
    'middle back': 'middle back',
    'forearms': 'forearms',
    'abductors': 'abductors',
    'adductors': 'adductors',
    'neck': 'neck',
    'full_body': 'full body', // map snake to space
    'full body': 'full body'
};

// Equipment: Polish display name -> DB key  
const EQUIPMENT_PL_TO_KEY: Record<string, string> = {
    'masa własnego ciała': 'bodyweight',
    'ciężar ciała': 'bodyweight',
    'body_only': 'body_only',
    'bodyweight': 'bodyweight',
    'sztanga': 'barbell',
    'barbell': 'barbell',
    'hantle': 'dumbbell',
    'dumbbell': 'dumbbell',
    'kettlebell': 'kettlebell',
    'maszyna': 'machine',
    'machine': 'machine',
    'guma oporowa': 'resistance_band',
    'gumy oporowe': 'resistance_band',
    'resistance_band': 'resistance_band',
    'bands': 'bands',
    'gumy': 'bands',
    'trx': 'trx',
    'taśmy': 'trx',
    'drążek': 'pull_up_bar',
    'pull_up_bar': 'pull_up_bar',
    'ławka': 'bench',
    'ławeczka': 'bench',
    'bench': 'bench',
    'mata': 'other',
    'maszyna smitha': 'machine',
    'wyciąg': 'cable',
    'cable': 'cable',
    'piłka lekarska': 'medicine_ball',
    'medicine_ball': 'medicine_ball',
    'piłka gimnastyczna': 'stability_ball',
    'stability_ball': 'stability_ball',
    'wałek do masażu': 'foam_roller',
    'foam_roller': 'foam_roller',
    'gryf łamany': 'ez_bar',
    'ez_bar': 'ez_bar',
    'e-z_curl_bar': 'e-z_curl_bar',
    'other': 'other',
    'brak': 'none',
    'none': 'none',
};

// Categories: Polish display name -> DB key
const CATEGORY_PL_TO_KEY: Record<string, string> = {
    'brzuch': 'core',
    'nogi': 'strength',
    'plecy': 'strength',
    'klatka': 'strength',
    'ramiona': 'strength',
    'core': 'core',
    'cardio': 'cardio',
    'siła': 'strength',
    'strength': 'strength',
    'stretching': 'stretching',
    'rozciąganie': 'stretching',
    'plyometrics': 'plyometrics',
    'plyometria': 'plyometrics',
    'powerlifting': 'powerlifting',
    'trójbój siłowy': 'powerlifting',
    'olympic_weightlifting': 'olympic_weightlifting',
    'podnoszenie ciężarów': 'olympic_weightlifting',
    'strongman': 'strongman',
    'flexibility': 'flexibility',
    'mobility': 'mobility',
    'other': 'other',
};

// Helper function to map Polish value to DB key
const mapToDbKey = (value: string, mapping: Record<string, string>): string => {
    const normalized = value.toLowerCase().trim();
    return mapping[normalized] || normalized;
};

/**
 * Lookup-table keys that don't match legacy values in exercise_library.equipment_needed.
 * Frontend sends the lookup key; we translate to the actual stored value before the API call.
 */
export const EQUIPMENT_KEY_TO_DB_VALUE: Record<string, string> = {
    kettlebell: 'kettlebells',
    foam_roller: 'foam roll',
};

const resolveEquipmentFilterValue = (equipmentKey: string): string => {
    const normalized = equipmentKey.toLowerCase().trim();
    return EQUIPMENT_KEY_TO_DB_VALUE[normalized] ?? equipmentKey;
};

/**
 * Exercise interface
 * 
 * LOCALIZATION STRATEGY:
 * - name, instructions: Stored in multiple languages (name_en, name_pl, name_de, etc.)
 * - category, equipment, muscles, force, mechanic, level: Stored as KEYS
 *   - Use *Display fields for translated values (e.g., categoryDisplay, primaryMusclesDisplay)
 *   - Keys are from lookup tables: muscle_groups, equipment_types, exercise_categories, etc.
 */
export interface Exercise {
    id: number | string;

    // Localized name - API returns translated based on ?lang= param
    name: string;
    name_en?: string;
    name_pl?: string;
    name_de?: string;

    // Difficulty level (key from difficulty_levels table)
    level: 'beginner' | 'intermediate' | 'advanced' | 'expert';

    // Muscle groups (keys from muscle_groups table)
    primaryMuscles: string[];           // Keys: ['chest', 'shoulders']
    primaryMusclesDisplay?: string[];   // Translated: ['Klatka piersiowa', 'Barki']
    secondaryMuscles?: string[];
    secondaryMusclesDisplay?: string[];

    /** EHSE v4.22 coefficients keyed by muscles.key, zeros omitted. Absent from list responses. */
    ehse?: Record<string, number> | null;
    /** Muscles trained at coefficient 1.0 — the purple chips, and `?muscleMatch=primary`. */
    ehsePrimaryGroups?: string[];
    /** Every muscle the exercise trains at all — the blue chips, and `?muscleMatch=involved`. */
    ehseGroups?: string[];

    // Media
    images: string[];

    // Localized instructions
    instructions: string[];
    instructions_en?: string[];
    instructions_pl?: string[];
    instructions_de?: string[];

    // Category (key from exercise_categories table)
    category: string;                   // Key: 'strength'
    categoryDisplay?: string;           // Translated: 'Siłowe'

    // Mechanic (key from mechanic_types table)
    mechanic: string;                   // Key: 'compound' | 'isolation'

    // Force type (key from force_types table)
    force: string;                      // Key: 'push' | 'pull' | 'static' | 'dynamic'

    // Equipment (keys from equipment_types table)
    equipment: string | string[];       // Keys: ['barbell', 'bench']
    equipmentDisplay?: string[];        // Translated: ['Sztanga', 'Ławka']

    // Ownership
    createdByTrainerId?: string;        // NULL = system/global, UUID = trainer's custom exercise
    isPublic?: boolean;                 // If false, only creator can see it
    sourceType?: 'system' | 'trainer' | 'imported';
}

export interface PaginatedExercises {
    data: Exercise[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

/**
 * Lookup table row interfaces
 */
export interface LookupTableRow {
    key: string;
    name_en: string;
    name_pl: string;
    name_de?: string;
}

export interface DifficultyLevelRow extends LookupTableRow {
    level_number: number;
}

export interface LookupTables {
    muscleGroups: LookupTableRow[];
    equipmentTypes: LookupTableRow[];
    exerciseCategories: LookupTableRow[];
    forceTypes: LookupTableRow[];
    mechanicTypes: LookupTableRow[];
    difficultyLevels: DifficultyLevelRow[];
}

const getAuthToken = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) return session.access_token;
    } catch (err) {
        console.warn('Failed to get Supabase session:', err);
    }
    return localStorage.getItem('token');
};

/**
 * How `?muscle=` matches, when it names one of the 27 muscles.
 *
 * 'involved' — the exercise trains it at any coefficient (`ehse_groups`).
 * 'primary'  — only at 1.0 (`ehse_primary_groups`).
 */
export type MuscleMatch = 'primary' | 'involved';

// ============================================
// CACHING LOGIC
// ============================================
// Cache storage: key -> { data: any, timestamp: number }. Map order = LRU order.
const exerciseCache = new Map<string, { data: PaginatedExercises | any[], timestamp: number }>();
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes — exercise data rarely changes; cache is cleared on mutations
const CACHE_MAX_ENTRIES = 60;

/** Page size of the exercise picker (issue #236, F13): first page on open, more on scroll. */
export const PICKER_PAGE_SIZE = 60;

// Account the cached pages belong to: lists include the account's private exercises,
// so every key carries it and switching accounts drops everything (issue #236, F06).
let cacheScope = 'anon';

// Generate cache key based on all filter parameters
export const generateCacheKey = (page: number, limit: number, muscle?: string, equipment?: string, force?: string, search?: string, onlyCreatedByMe?: boolean, lang?: string, muscleMatch?: MuscleMatch, scope: string = cacheScope) => {
    // muscleMatch is part of the key: 'gmax_low' as a primary muscle and as an
    // involved one are two different result sets (379 exercises vs 989), and
    // leaving it out would serve one for the other when the toggle flips.
    return `exercises_${scope}_${page}_${limit}_${muscle || 'all'}_${muscleMatch || 'involved'}_${equipment || 'all'}_${force || 'all'}_${search || ''}_${onlyCreatedByMe}_${lang || 'en'}`;
};

const readCache = (key: string) => {
    const cached = exerciseCache.get(key);
    if (!cached) return null;
    if (Date.now() - cached.timestamp >= CACHE_TTL) {
        exerciseCache.delete(key);
        return null;
    }
    exerciseCache.delete(key); // refresh LRU position
    exerciseCache.set(key, cached);
    return cached.data;
};

const writeCache = (key: string, data: PaginatedExercises | any[]) => {
    exerciseCache.delete(key);
    exerciseCache.set(key, { data, timestamp: Date.now() });
    while (exerciseCache.size > CACHE_MAX_ENTRIES) {
        const oldest = exerciseCache.keys().next().value;
        if (oldest === undefined) break;
        exerciseCache.delete(oldest);
    }
};

// Clear cache (used on mutations and on account change)
export const clearExerciseCache = () => {
    exerciseCache.clear();
    inflightRequests.clear();
};

/** Called by AuthContext on every auth state: a different account starts from an empty cache. */
export const setExerciseCacheScope = (userId: string | null | undefined) => {
    const next = userId || 'anon';
    if (next === cacheScope) return;
    cacheScope = next;
    clearExerciseCache();
};

// In-flight request deduplication: cacheKey -> Promise
const inflightRequests = new Map<string, Promise<PaginatedExercises>>();

/** First picker page for the default filters, if cached (no network call). */
export const getCachedExercises = (lang: string = 'pl'): PaginatedExercises | null =>
    (readCache(generateCacheKey(1, PICKER_PAGE_SIZE, 'Wszystkie', undefined, undefined, undefined, undefined, lang)) as PaginatedExercises | null);

/** Maps one exercise of the API (list or detail) to the frontend shape. */
const mapApiExercise = (ex: any): Exercise => {
    // Handle images if they come as image1Url, image2Url, etc.
    let images = ex.images || [];
    if (!images.length) {
        if (ex.image1Url) images.push(ex.image1Url);
        if (ex.image2Url) images.push(ex.image2Url);
    }
    return {
        id: ex.id,
        // Localized name (API returns translated name based on lang param)
        name: ex.name,
        name_en: ex.name_en,
        name_pl: ex.name_pl,
        name_de: ex.name_de,
        level: ex.level || 'intermediate',
        // Keys from lookup tables
        primaryMuscles: ex.primaryMuscles || [],
        // Translated display values (for UI)
        primaryMusclesDisplay: ex.primaryMusclesDisplay || ex.primaryMuscles || [],
        secondaryMuscles: ex.secondaryMuscles || [],
        secondaryMusclesDisplay: ex.secondaryMusclesDisplay || ex.secondaryMuscles || [],
        ehse: ex.ehse || null,
        ehsePrimaryGroups: ex.ehsePrimaryGroups || [],
        ehseGroups: ex.ehseGroups || [],
        images: images,
        // Localized instructions
        instructions: ex.instructions || [],
        instructions_en: ex.instructions_en,
        instructions_pl: ex.instructions_pl,
        instructions_de: ex.instructions_de,
        // Keys with display values
        category: ex.category || 'strength',
        categoryDisplay: ex.categoryDisplay || ex.category || 'Other',
        mechanic: ex.mechanic || 'compound',
        force: ex.force || 'push',
        equipment: ex.equipment || [],
        equipmentDisplay: ex.equipmentDisplay || ex.equipment || [],
        createdByTrainerId: ex.createdByTrainerId,
        isPublic: ex.isPublic,
        sourceType: ex.sourceType,
    } as Exercise;
};

export const exerciseService = {
    getExercises: async (page: number = 1, limit: number = 12, muscle?: string, equipment?: string, force?: string, search?: string, onlyCreatedByMe?: boolean, lang?: string, muscleMatch: MuscleMatch = 'involved'): Promise<PaginatedExercises> => {
        // 1. Check Cache
        const cacheKey = generateCacheKey(page, limit, muscle, equipment, force, search, onlyCreatedByMe, lang, muscleMatch);
        const cached = readCache(cacheKey);
        if (cached) return cached as PaginatedExercises;

        // 2. Deduplicate in-flight requests (e.g. preload + modal open simultaneously)
        const inflight = inflightRequests.get(cacheKey);
        if (inflight) {
            console.log('[ExerciseService] Joining in-flight request:', cacheKey);
            return inflight;
        }

        const scopeAtStart = cacheScope;
        const fetchPromise = (async (): Promise<PaginatedExercises> => {

        const token = await getAuthToken();
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        let url = `${API_URL}/exercises?page=${page}&limit=${limit}`;
        if (muscle && muscle !== 'Wszystkie' && muscle !== 'All') {
            url += `&muscle=${encodeURIComponent(muscle)}`;
            // Ignored by the backend unless `muscle` is one of the 27 muscle keys.
            url += `&muscleMatch=${muscleMatch}`;
        }
        if (equipment && equipment !== 'Wszystkie' && equipment !== 'All') {
            const equipmentParam = resolveEquipmentFilterValue(equipment);
            url += `&equipment=${encodeURIComponent(equipmentParam)}`;
        }
        if (force && force !== 'Wszystkie' && force !== 'All') {
            url += `&force=${encodeURIComponent(force)}`;
        }
        if (search && search.trim()) {
            url += `&search=${encodeURIComponent(search.trim())}`;
        }
        if (onlyCreatedByMe) {
            url += `&onlyCreatedByMe=true`;
        }
        if (lang) {
            url += `&lang=${encodeURIComponent(lang)}`;
        }
        // Request the slim projection (no instruction/description blobs); the detail/edit
        // modals hydrate the full record via getExerciseById on open.
        url += `&slim=true`;

        const res = await fetch(url, { headers });

        if (!res.ok) {
            throw new Error(`Failed to fetch exercises: ${res.status}`);
        }

        const responseData = await res.json();

        // Handle both array (legacy/fallback) and paginated object response
        let exercisesData: any[] = [];
        let total = 0;
        let currentPage = page;
        let currentLimit = limit;
        let totalPages = 1;

        if (Array.isArray(responseData)) {
            exercisesData = responseData;
            total = responseData.length;
        } else {
            exercisesData = responseData.data || [];
            total = responseData.total || 0;
            currentPage = responseData.page || page;
            currentLimit = responseData.limit || limit;
            totalPages = responseData.totalPages || 1;
        }

        // Map backend data to frontend interface
        const mappedExercises = exercisesData.map(mapApiExercise);

        const result = {
            data: mappedExercises,
            total: total,
            page: currentPage,
            limit: currentLimit,
            totalPages: totalPages
        };

        // 2. Save to Cache (only if the account didn't change meanwhile)
        if (scopeAtStart === cacheScope) writeCache(cacheKey, result);

        return result;
        })();

        inflightRequests.set(cacheKey, fetchPromise);
        fetchPromise.finally(() => inflightRequests.delete(cacheKey));
        return fetchPromise;
    },

    /**
     * Fetch a single exercise's full detail from the backend (GET /exercises/:id).
     * The list endpoint omits the heavy instruction/description blobs to keep payloads
     * small, so the detail modal hydrates them on open via this call.
     */
    getExerciseById: async (id: string, lang?: string): Promise<Partial<Exercise> | null> => {
        try {
            const token = await getAuthToken();
            const headers: Record<string, string> = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            let url = `${API_URL}/exercises/${id}`;
            if (lang) url += `?lang=${encodeURIComponent(lang)}`;

            const res = await fetch(url, { headers });
            if (!res.ok) throw new Error(`Failed to fetch exercise ${id}: ${res.status}`);

            const ex = await res.json();
            return {
                images: ex.images || [],
                instructions: ex.instructions || [],
                instructions_en: ex.instructions_en,
                instructions_pl: ex.instructions_pl,
                instructions_de: ex.instructions_de,
                // The list projection leaves `ehse` out — 3168 coefficient maps
                // is payload no card needs. The details modal shows them, so it
                // picks them up here on open.
                ehse: ex.ehse ?? null,
                ehsePrimaryGroups: ex.ehsePrimaryGroups || [],
                ehseGroups: ex.ehseGroups || [],
            };
        } catch (error) {
            console.error('[ExerciseService] getExerciseById failed:', error);
            return null;
        }
    },

    /**
     * One exercise as a picker card (name, muscles, images) — used to show the currently
     * selected exercise when it isn't on the loaded pages, without loading the library.
     */
    getExerciseCard: async (id: string, lang?: string): Promise<Exercise | null> => {
        try {
            const token = await getAuthToken();
            const headers: Record<string, string> = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            let url = `${API_URL}/exercises/${encodeURIComponent(id)}`;
            if (lang) url += `?lang=${encodeURIComponent(lang)}`;
            const res = await fetch(url, { headers });
            if (!res.ok) return null;
            return mapApiExercise(await res.json());
        } catch {
            return null;
        }
    },

    /**
     * Get lookup tables for exercise attributes (muscles, equipment, categories, etc.)
     * These are used for translations and filtering options
     */
    getLookupTables: async (): Promise<{
        muscleGroups: Array<{ key: string; name_en: string; name_pl: string; name_de?: string }>;
        equipmentTypes: Array<{ key: string; name_en: string; name_pl: string; name_de?: string }>;
        exerciseCategories: Array<{ key: string; name_en: string; name_pl: string; name_de?: string }>;
        forceTypes: Array<{ key: string; name_en: string; name_pl: string; name_de?: string }>;
        mechanicTypes: Array<{ key: string; name_en: string; name_pl: string; name_de?: string }>;
        difficultyLevels: Array<{ key: string; name_en: string; name_pl: string; name_de?: string; level_number: number }>;
    }> => {
        try {
            const res = await fetch(`${API_URL}/exercises/lookup-tables`);
            if (!res.ok) {
                throw new Error(`Failed to fetch lookup tables: ${res.status}`);
            }
            return await res.json();
        } catch (error) {
            console.error('Failed to fetch lookup tables:', error);
            // Return empty tables as fallback
            return {
                muscleGroups: [],
                equipmentTypes: [],
                exerciseCategories: [],
                forceTypes: [],
                mechanicTypes: [],
                difficultyLevels: [],
            };
        }
    },

    addExercise: async (exercise: {
        name: string;
        level: string;
        category: string;
        mechanic?: string;
        force?: string;
        equipment?: string;
        primaryMuscles: string[];  // Can be keys or translated names
        secondaryMuscles?: string[];
        /** EHSE coefficients from the matrix editor. The trigger derives the rest. */
        ehse?: Record<string, number>;
        instructions: string[];
        images?: string[]; // Legacy
        imageFiles?: File[]; // For upload
        language?: string; // Current UI language
    }): Promise<void> => {
        // Get current user for created_by_trainer_id
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
            throw new Error('Not authenticated');
        }

        // Clear cache as we're adding new data
        clearExerciseCache();

        // Use imageUrls if present (legacy), otherwise fallback to existing images
        // const finalImages = exercise.images || [];

        // Map Polish UI values to English DB keys using mapping tables
        const primaryMuscleGroups = exercise.primaryMuscles.map(m => mapToDbKey(m, MUSCLE_PL_TO_KEY));
        const secondaryMuscleGroups = (exercise.secondaryMuscles || []).map(m => mapToDbKey(m, MUSCLE_PL_TO_KEY));
        const equipmentKey = exercise.equipment ? mapToDbKey(exercise.equipment, EQUIPMENT_PL_TO_KEY) : 'bodyweight';
        const categoryKey = exercise.category ? mapToDbKey(exercise.category, CATEGORY_PL_TO_KEY) : 'strength';

        // Prepare payload for new API
        const lang = exercise.language || 'pl';

        const apiPayload: any = {
            name: exercise.name,
            category: categoryKey,
            level: exercise.level?.toLowerCase() || 'intermediate',
            mechanic: exercise.mechanic?.toLowerCase() || 'compound',
            force: exercise.force?.toLowerCase() || 'push',
            primaryMuscleGroups: primaryMuscleGroups,
            secondaryMuscleGroups: secondaryMuscleGroups,
            equipmentNeeded: [equipmentKey],
            isPublic: false,
        };

        // Without this a trainer-authored exercise lands with ehse = NULL: no
        // muscle chips, invisible to every muscle filter, and contributing
        // nothing to the volume chart. updateExercise has always sent it, so the
        // exercise only came right after an edit that changed nothing.
        if (exercise.ehse && Object.keys(exercise.ehse).length > 0) {
            apiPayload.ehse = exercise.ehse;
        }

        const instructionsArray = exercise.instructions
            ? (Array.isArray(exercise.instructions) ? exercise.instructions : [exercise.instructions])
            : [];

        // Set localized name and instructions based on language
        if (lang === 'pl') {
            apiPayload.name_pl = exercise.name;
            apiPayload.instructions_pl = instructionsArray;
        } else if (lang === 'de') {
            apiPayload.name_de = exercise.name;
            apiPayload.instructions_de = instructionsArray;
        } else {
            apiPayload.instructions = instructionsArray;
        }

        // Call backend API instead of direct Supabase insert
        const token = await getAuthToken();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // If we have images, use FormData
        // We now rely on backend to upload to R2, so we just check if we have files to send
        if (exercise.imageFiles && exercise.imageFiles.length > 0) {
            const formData = new FormData();
            Object.entries(apiPayload).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(item => formData.append(key, String(item)));
                } else if (typeof value === 'boolean') {
                    // skip — isPublic is optional and backend defaults it
                } else if (value !== null && typeof value === 'object') {
                    // `ehse` is a coefficient map. String(value) would send
                    // "[object Object]"; the DTO's @toEhseMap() expects JSON.
                    formData.append(key, JSON.stringify(value));
                } else if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            });

            // Add image files
            exercise.imageFiles.forEach((file) => {
                formData.append('images', file);
            });

            const res = await fetch(`${API_URL}/exercises`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to create exercise: ${res.status}`);
            }
        } else {
            const res = await fetch(`${API_URL}/exercises`, {
                method: 'POST',
                headers,
                body: JSON.stringify(apiPayload)
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to create exercise: ${res.status}`);
            }
        }
    },

    deleteExercise: async (exerciseId: string): Promise<void> => {
        const token = await getAuthToken();
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Clear cache
        clearExerciseCache();

        const res = await fetch(`${API_URL}/exercises/${exerciseId}`, {
            method: 'DELETE',
            headers
        });

        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to delete exercise: ${res.status}`);
        }
    },

    updateExercise: async (exerciseId: string, exercise: Partial<Exercise>): Promise<void> => {
        const token = await getAuthToken();

        // Clear cache
        clearExerciseCache();

        // Check if we have image files to upload
        const imageFiles = (exercise as any).imageFiles;

        if (imageFiles && imageFiles.length > 0) {
            // Use FormData for file uploads
            const formData = new FormData();

            // Add exercise data as JSON
            const exerciseData = { ...exercise };
            delete (exerciseData as any).imageFiles;
            formData.append('data', JSON.stringify(exerciseData));

            // Add image files
            imageFiles.forEach((file: File) => {
                formData.append('images', file);
            });

            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const res = await fetch(`${API_URL}/exercises/${exerciseId}`, {
                method: 'PUT',
                headers,
                body: formData
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to update exercise: ${res.status}`);
            }
        } else {
            // No files, use JSON
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const exerciseData = { ...exercise };
            delete (exerciseData as any).imageFiles;

            const res = await fetch(`${API_URL}/exercises/${exerciseId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(exerciseData)
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to update exercise: ${res.status}`);
            }
        }
    },

    getCurrentUserId: async (): Promise<string | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        return user?.id || null;
    },

    /**
     * EHSE coefficients for exercises picked during plan editing.
     *
     * The list endpoint deliberately leaves `ehse` out of its slim projection —
     * the picker loads up to 10 000 exercises and no card renders coefficients.
     * A plan loaded from Supabase carries them (PLAN_FULL_SELECT), but an
     * exercise the trainer has just swapped in does not, and without this it
     * would be missing from the muscle chart until the plan is saved and
     * reloaded. There is no legacy fallback to soften that any more: at muscle
     * resolution, no `ehse` means no bar.
     *
     * Read straight from the table — one PK lookup, and exercise_library is
     * readable by any authenticated user.
     */
    getEhseByIds: async (ids: string[]): Promise<Record<string, Record<string, number>>> => {
        const unique = [...new Set(ids.filter(Boolean))];
        if (unique.length === 0) return {};

        const { data, error } = await supabase
            .from('exercise_library')
            .select('id, ehse')
            .in('id', unique);

        if (error) {
            console.warn('[ExerciseService] getEhseByIds failed, falling back to legacy muscles:', error.message);
            return {};
        }

        const out: Record<string, Record<string, number>> = {};
        for (const row of data || []) {
            if (row.ehse) out[String(row.id)] = row.ehse as Record<string, number>;
        }
        return out;
    },

    /**
     * Prefetch adjacent pages so navigation feels instant.
     * Runs silently in the background — errors are swallowed.
     */
    prefetchAdjacentPages: (currentPage: number, totalPages: number, limit: number, muscle?: string, equipment?: string, force?: string, search?: string, onlyCreatedByMe?: boolean, lang?: string) => {
        const pagesToPrefetch: number[] = [];
        if (currentPage > 1) pagesToPrefetch.push(currentPage - 1);
        if (currentPage < totalPages) pagesToPrefetch.push(currentPage + 1);

        for (const page of pagesToPrefetch) {
            const cacheKey = generateCacheKey(page, limit, muscle, equipment, force, search, onlyCreatedByMe, lang);
            const cached = exerciseCache.get(cacheKey);
            if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
                continue; // already cached
            }
            // Fire-and-forget prefetch
            exerciseService.getExercises(page, limit, muscle, equipment, force, search, onlyCreatedByMe, lang).catch(() => { });
        }
    }
};
